import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box, Typography, Paper, Stack, TextField, Tooltip, CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { updateMachineModel, createMachineModel, getMachinesByModel } from "../../../features/machines/actions";
import { readAsyncStorageValues } from "../../../features/common/actions";
import moment from "moment";

const AddEditMachineModel = () => {
  const dispatch = useDispatch();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { machineModels, asyncStorageValues } = useSelector((state) => state.common);
  const { customerSerialNumberView } = useSelector((state) => state.machines);

  const [enableEdit, setEnableEdit] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    dispatch(readAsyncStorageValues());
    setIsDataLoading(false);
  }, [dispatch]);

  useEffect(() => {
    dispatch(getMachinesByModel(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      const selectedModel = machineModels.find((item) => item.id == id) || {};
      let infoData = selectedModel.info;
      if (typeof infoData === "string") {
        try {
          infoData = JSON.parse(infoData);
        } catch (error) {
          console.error("Error parsing info JSON:", error);
          infoData = {};
        }
      }
      setFormData({
        ...selectedModel,
        info: {
          ...infoData,
          notes: infoData?.notes || "",
          tonnage: infoData?.tonnage || 0,
        },
      });
    }
    if (state?.enableEdit || !id) setEnableEdit(true);
  }, [id, machineModels, state]);

  const handleSaveButton = () => {
    const tonnageTrimmed = typeof formData.info?.tonnage === "string" ? formData.info?.tonnage.trim() : formData.info?.tonnage;
    const roundTonnage = (val, decimals = 1) => {
      const num = parseFloat(val);
      if (isNaN(num)) return "";
      return parseFloat(num.toFixed(decimals));
    };
    const tonnageValue = roundTonnage(tonnageTrimmed, 1);

    if (!formData.manufacturer || !formData.model || isNaN(tonnageValue) || tonnageValue <= 0) {
      setErrors({
        model: formData.model || "Please enter model!",
        manufacturer: formData.manufacturer || "Please enter manufacturer!",
        tonnage: formData.info?.tonnage || "Please enter a valid tonnage number!",
      });
      return toast.warn(`Please enter required fields!`);
    }

    let mergedInfo = { notes: formData.info?.notes ?? "", tonnage: tonnageValue };

    const existingModel = machineModels.find(
      (item) =>
        item.model.toLowerCase().trim() === formData.model.toLowerCase().trim() &&
        item.manufacturer.toLowerCase().trim() === formData.manufacturer.toLowerCase().trim() &&
        item.id !== id
    );

    if (existingModel) {
      const existingInfo = typeof existingModel.info === "string" ? JSON.parse(existingModel.info || "{}") : existingModel.info || {};

      mergedInfo = {
        ...existingInfo,
        tonnage: tonnageValue,
        notes: formData.info?.notes ?? "",
      };

      const existingNotes = existingInfo.notes === formData.info?.notes ?? "";
      const existingTonnage = Number(existingInfo.tonnage) === tonnageValue;

      if (
        existingNotes &&
        existingTonnage &&
        existingModel.manufacturer.toLowerCase() === formData.manufacturer.toLowerCase() &&
        existingModel.model.toLowerCase() === formData.model.toLowerCase()
      ) {
        return toast.warn(`Model already exists with the same manufacturer, model name, and notes!`);
      }
    }
    const updateObj = {
      manufacturer: formData.manufacturer,
      modelName: formData.model,
      info: JSON.stringify(mergedInfo),
    };

    if (id) {
      updateObj.modelId = id;
      submitForm(updateMachineModel, updateObj);
    } else {
      submitForm(createMachineModel, updateObj);
    }
  };

  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));
  const columns = [
    { field: "customerName", headerName: "Customer Name", flex: 1, minWidth: 300 },
    { field: "serialNumber", headerName: "Serial Number", flex: 1, minWidth: 300 },
    {
      field: "warrantyExpiry",
      headerName: "Warranty",
      flex: 1,
      minWidth: 300,
      sortable: true,
      sortComparator: dateComparator,
    },
    {
      field: "amcExpiry",
      headerName: "AMC",
      flex: 1,
      minWidth: 300,
      sortable: true,
      sortComparator: dateComparator,
    },
    {
      field: "col6",
      headerName: "",
      width: 150,
      disableClickEventBubbling: false,
      sortable: false,
      filterable: false,
    },
  ];

  const rows =
    customerSerialNumberView?.map((item, index) => ({
      id: item.id || index,
      customerName: item.customerName || "-",
      serialNumber: item.serialNumber || "-",
      amcExpiry: item.amcExpiry ? moment(item.amcExpiry).format("DD MMM YYYY") : "-",
      warrantyExpiry: item.warrantyExpiry ? moment(item.warrantyExpiry).format("DD MMM YYYY") : "-",
    })) || [];

  const inputChangeHandler = (event) => {
    const numberRegex = /^[0-9+().\-\s]*$/;
    let id = event.target.id;
    let value = event.target.value;
    if (id === "tonnage") {
      if (value && !numberRegex.test(value)) {
        setValidationErrors((prevState) => ({ ...prevState, tonnage: "Please enter a valid tonnage number" }));
      } else {
        setValidationErrors((prevState) => ({ ...prevState, tonnage: null }));
      }
    }
  };

  const submitForm = (action, updateObj) => {
    dispatch(action(updateObj)).then((res) => {
      dispatch(readAsyncStorageValues());
      setEnableEdit(false);
      if (res?.payload?.status && res?.payload?.data) {
        navigate(`/edit-machine-model/` + res.payload.data.id, { replace: true });
        return toast.success(` Machine Model ${id ? "updated" : "created"}`, { autoClose: 1000 });
      } else return toast.warn(` Please try again!`);
    });
  };
  return (
    <div>
      {enableEdit ? (
        <Stack direction={"row"} sx={{ marginY: 5, marginLeft: "4%", height: "auto" }}>
          <Stack gap={1} flexWrap={"wrap"} sx={{ marginRight: 2 }}>
            <TextField
              style={{ boxShadow: "0px 3px 15px rgba(0,0,0,0.1)" }}
              value={formData.manufacturer}
              id="manufacturer"
              label="Manufacturer"
              name="manufacturer"
              InputProps={{
                inputProps: { maxLength: 32 },
              }}
              onChange={(newValue) => setFormData({ ...formData, manufacturer: newValue.target.value.toUpperCase() })}
              sx={{ minWidth: 350 }}
              error={!formData.manufacturer && errors.manufacturer}
              helperText={!formData.manufacturer && errors.manufacturer}
            />
            <Typography variant="caption" color="textSecondary" sx={{ marginLeft: 25 }}>
              {32 - (formData.manufacturer?.length || 0)} characters remaining
            </Typography>
            <TextField
              style={{ boxShadow: "0px 3px 15px rgba(0,0,0,0.1)" }}
              margin="normal"
              value={formData.model}
              id="machine model"
              label="Machine Model"
              name="modelName"
              InputProps={{
                inputProps: { maxLength: 32 },
              }}
              onChange={(newValue) => setFormData({ ...formData, model: newValue.target.value.toUpperCase() })}
              sx={{ minWidth: 350 }}
              error={!formData.model && errors.model}
              helperText={!formData.model && errors.model}
            />
            <Typography variant="caption" color="textSecondary" sx={{ marginLeft: 25 }}>
              {Math.max(0, 32 - (formData.model?.length || 0))} characters remaining
            </Typography>
            {asyncStorageValues?.acIndustry === true && (
              <TextField
                margin="normal"
                value={formData.info?.tonnage || ""}
                id="tonnage"
                label="Tonnage"
                name="tonnage"
                onChange={(event) => {
                  setFormData((prev) => ({
                    ...prev,
                    info: {
                      ...(prev.info || {}),
                      tonnage: event.target.value,
                    },
                  }));
                  inputChangeHandler(event);
                }}
                error={Boolean(validationErrors.tonnage) || (!formData.info?.tonnage && errors.tonnage)}
                helperText={validationErrors.tonnage || (!formData.info?.tonnage && errors.tonnage) || ""}
              />
            )}
            <TextField
              style={{ boxShadow: "0px 3px 15px rgba(0,0,0,0.1)" }}
              margin="normal"
              value={formData.info?.notes || ""}
              id="notes"
              label="Notes"
              name="notes"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  info: {
                    ...(prev.info || {}),
                    notes: e.target.value,
                  },
                }))
              }
              sx={{ minWidth: 350 }}
            />
          </Stack>
          <Stack>
            <Tooltip title="Save">
              <Button variant="contained" onClick={handleSaveButton}>
                Save
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      ) : (
        <>
          <Box sx={{ mx: 3, marginY: 2, marginLeft: "3%" }}>
            <Paper sx={{ borderRadius: 2, mb: 1.5, p: 3 }}>
              <Stack justifyContent={"space-between"} direction={"row"} flexWrap={"wrap"}>
                <Stack rowGap={1}>
                  <Typography>
                    <span style={{ fontWeight: "bold" }}>Manufacturer:</span> {formData.manufacturer}
                  </Typography>
                  <Typography>
                    <span style={{ fontWeight: "bold" }}>Model:</span> {formData.model}
                  </Typography>
                  {asyncStorageValues?.acIndustry === true && (
                    <Typography>
                      <span style={{ fontWeight: "bold" }}>Tonnage:</span> {formData.info?.tonnage || ""}
                    </Typography>
                  )}
                  <Typography>
                    <span style={{ fontWeight: "bold" }}>Notes:</span> {formData.info?.notes || ""}
                  </Typography>
                </Stack>
                <Stack rowGap={1}>
                  <Tooltip title="Edit model">
                    <Button onClick={() => setEnableEdit(true)} variant="contained">
                      Edit
                    </Button>
                  </Tooltip>
                </Stack>
              </Stack>
            </Paper>
          </Box>
          <Box sx={{ height: 600, mx: 3, marginY: 2, marginLeft: "3%" }}>
            {isDataLoading ? (
              <CircularProgress
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            ) : (
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={5}
                localeText={{ noRowsLabel: "No Data." }}
                style={{ boxShadow: "0px 3px 15px rgba(0,0,0,0.2)", marginLeft: 5 }}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "customerName", sort: "asc" }],
                  },
                }}
              />
            )}
          </Box>
        </>
      )}
      <ToastContainer />
    </div>
  );
};

export default AddEditMachineModel;
